import React from "react";
import {
  Box,
  Container,
  Grid,
  Skeleton,
  Card,
  CardContent,
} from "@mui/material";
import { gradients } from "../../utils/theme";

/**
 * MallHubSkeleton Component
 *
 * Full-page skeleton loading state for MallHub screen.
 * Shows accurate placeholders for Hero, Quick Actions, and all sections.
 */
const MallHubSkeleton: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)",
      }}
    >
      {/* Hero Skeleton */}
      <Box
        sx={{
          background: gradients.navyLux,
          pt: { xs: 10, md: 12 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Skeleton
              variant="text"
              width="60%"
              sx={{
                fontSize: "3.5rem",
                bgcolor: "rgba(255,255,255,0.2)",
                mx: "auto",
                mb: 2,
              }}
            />
            <Skeleton
              variant="text"
              width="40%"
              sx={{
                fontSize: "1.25rem",
                bgcolor: "rgba(255,255,255,0.2)",
                mx: "auto",
                mb: 4,
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {[...Array(4)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={120}
                  height={32}
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: 3 }}
                />
              ))}
            </Box>
            {/* Search Bar Skeleton */}
            <Box
              sx={{
                maxWidth: 700,
                mx: "auto",
                mt: 4,
                width: "100%",
                px: { xs: 2, sm: 0 },
              }}
            >
              <Skeleton
                variant="rounded"
                height={56}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 3,
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content Skeleton */}
      <Box
        sx={{
          bgcolor: "white",
          py: { xs: 3, md: 4 },
          borderTopLeftRadius: { xs: 16, md: 24 },
          borderTopRightRadius: { xs: 16, md: 24 },
          mt: -3,
        }}
      >
        <Container maxWidth="lg">
          {/* Quick Action Cards Skeleton */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[...Array(3)].map((_, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Skeleton variant="circular" width={48} height={48} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={28} />
                        <Skeleton variant="text" width="40%" height={20} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Offers Section Skeleton */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mr: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="30%" height={32} />
                <Skeleton variant="text" width="50%" height={20} />
              </Box>
            </Box>

            {/* Mobile: Horizontal Scroll Skeleton */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                overflowX: "auto",
                gap: 2,
                pb: 2,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {[...Array(3)].map((_, index) => (
                <Box key={index} sx={{ minWidth: { xs: "85%", sm: "45%" } }}>
                  <Card sx={{ height: "100%", borderRadius: 2 }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent sx={{ p: 2 }}>
                      <Skeleton
                        variant="rounded"
                        width="40%"
                        height={24}
                        sx={{ mb: 1.5, borderRadius: 3 }}
                      />
                      <Skeleton
                        variant="text"
                        width="90%"
                        sx={{ fontSize: "1.5rem", mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="70%"
                        sx={{ fontSize: "1.5rem", mb: 2 }}
                      />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

            {/* Desktop: Grid Skeleton */}
            <Grid
              container
              spacing={3}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ height: "100%", borderRadius: 2 }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent sx={{ p: 2 }}>
                      <Skeleton
                        variant="rounded"
                        width="40%"
                        height={24}
                        sx={{ mb: 1.5, borderRadius: 3 }}
                      />
                      <Skeleton
                        variant="text"
                        width="90%"
                        sx={{ fontSize: "1.5rem", mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="70%"
                        sx={{ fontSize: "1.5rem", mb: 2 }}
                      />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Events Section Skeleton */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mr: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="35%" height={32} />
                <Skeleton variant="text" width="55%" height={20} />
              </Box>
            </Box>

            {/* Mobile: Horizontal Scroll Skeleton */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                overflowX: "auto",
                gap: 2,
                pb: 2,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {[...Array(3)].map((_, index) => (
                <Box key={index} sx={{ minWidth: { xs: "85%", sm: "45%" } }}>
                  <Card sx={{ height: "100%", borderRadius: 2 }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent sx={{ p: 2 }}>
                      <Skeleton
                        variant="rounded"
                        width="40%"
                        height={24}
                        sx={{ mb: 1.5, borderRadius: 3 }}
                      />
                      <Skeleton
                        variant="text"
                        width="90%"
                        sx={{ fontSize: "1.5rem", mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="70%"
                        sx={{ fontSize: "1.5rem", mb: 2 }}
                      />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                      <Skeleton variant="text" width="55%" />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

            {/* Desktop: Grid Skeleton */}
            <Grid
              container
              spacing={3}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ height: "100%", borderRadius: 2 }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent sx={{ p: 2 }}>
                      <Skeleton
                        variant="rounded"
                        width="40%"
                        height={24}
                        sx={{ mb: 1.5, borderRadius: 3 }}
                      />
                      <Skeleton
                        variant="text"
                        width="90%"
                        sx={{ fontSize: "1.5rem", mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="70%"
                        sx={{ fontSize: "1.5rem", mb: 2 }}
                      />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                      <Skeleton variant="text" width="55%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Stores Section Skeleton */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mr: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="25%" height={32} />
                <Skeleton variant="text" width="45%" height={20} />
              </Box>
            </Box>

            {/* Mobile: Horizontal Scroll Skeleton */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                overflowX: "auto",
                gap: 2,
                pb: 2,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {[...Array(4)].map((_, index) => (
                <Box key={index} sx={{ minWidth: { xs: "70%", sm: "40%" } }}>
                  <Card sx={{ height: "100%", borderRadius: 2 }}>
                    <Skeleton variant="rectangular" height={140} />
                    <CardContent sx={{ p: 2 }}>
                      <Skeleton
                        variant="text"
                        width="70%"
                        sx={{ fontSize: "1.25rem", mb: 1 }}
                      />
                      <Skeleton variant="text" width="50%" sx={{ mb: 1.5 }} />
                      <Skeleton
                        variant="rounded"
                        width="60%"
                        height={24}
                        sx={{ borderRadius: 3 }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>

            {/* Desktop: Grid Skeleton */}
            <Grid
              container
              spacing={3}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card sx={{ height: "100%", borderRadius: 2 }}>
                    <Skeleton variant="rectangular" height={140} />
                    <CardContent sx={{ p: 2 }}>
                      <Skeleton
                        variant="text"
                        width="70%"
                        sx={{ fontSize: "1.25rem", mb: 1 }}
                      />
                      <Skeleton variant="text" width="50%" sx={{ mb: 1.5 }} />
                      <Skeleton
                        variant="rounded"
                        width="60%"
                        height={24}
                        sx={{ borderRadius: 3 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default MallHubSkeleton;
